import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
   Tabs,
   useDidUpdateEffect,
   useDidUpdateDeepCompareLayoutEffect,
   ToastList,
   useSelectGroup,
} from '@devesharp/react/web';
import dateFNS from 'date-fns';
import { FormHandles } from '@unform/core';
import { useRecoilState } from 'recoil';
import {
   APIServiceUrl,
   BoletoCancelManyResolve,
   BoletoCancelResolve,
   CustomersSearchResolve,
   DataDashResolve,
   GenerateExcel,
   GroupsSearchResolve,
   PaymentsSearchResolve,
   SendWhatsAppBoletoResolve,
} from '~/api';
import { formatPrice } from '~/services';
import { useViewList } from '~/hooks/useViewList';
import { useUser } from '~/hooks';
import { companySelectState, companyShowInfoState } from '~/recoil/company-select.state';
import { fileDownloadGlobalState } from '~/recoil/file-donwload-global.state';

const grahSettings = {
   data: {
      labels: [''],
      datasets: [
         {
            label: '# of Votes',
            data: [100],
            backgroundColor: ['#eee'],
         },
      ],
   },
   options: {
      cutoutPercentage: 75,
      legend: {
         display: false,
      },
      tooltips: {
         callbacks: {
            title(tooltipItem: any, data: any) {
               return data.labels[tooltipItem[0].index];
            },
            label() {
               return '';
            },
            afterLabel(tooltipItem: any, data: any) {
               return formatPrice(data?.prices[tooltipItem?.index] ?? 0, 0);
            },
         },
      },
   },
   height: 250,
};

export function useBoletosListingPage(): any {
   document.title = 'Boletos - XPAG Tecnologia';
   const [showSeller] = useRecoilState(companyShowInfoState);

   const {
      starting,
      searching,
      errorLoadData,
      reloadPage,
      registerResolveParams,
      registerOnInit,
      registerOnAfterSearch,
      resources,
      resourcesTotal,
      skeletonResources,
      setFilters,
      setOffset,
      registerOnSearch,
      setPage,
      setSort,
      graph,
      filters,
   } = useViewList({
      resolves: {
         items: PaymentsSearchResolve,
         groups: GroupsSearchResolve,
      },
      filtersDefault: {
         payment_type: 'boleto',
         dash: true,
      },
      handleQueryToFilter: (query: any) => {
         if (query.payment_type_array) {
            if (typeof query.payment_type_array !== 'object') {
               query.payment_type_array = [query.payment_type_array];
            }
         }
         return query;
      },
   }) as any;

   const [sum, setSum] = useState(0);
   const [sumPage, setSumPage] = useState(0);
   const [expiredGraphProps, setExpiredGraphProps] = useState<any>(grahSettings);
   const [toExpireGraphProps, setToExpireGraphProps] = useState<any>(grahSettings);
   const [losersBar, setLosersBar] = useState<any[]>([]);
   const [toWinBar, setToWinBar] = useState<any[]>([]);
   const [statement, setStatement] = useState([]);
   const [fileDownloadGlobal, setFileDownloadGlobal] = useRecoilState(fileDownloadGlobalState);

   const [onSelect, onUnselectAll, selected, checkSelected] = useSelectGroup();

   const [resource, setResource] = useState<any>();
   const [groups, setGroups] = useState<any[]>([]);
   const [loading, setLoading] = useState(false);
   const [currentPage, setCurrentPage] = useState(parseInt(((filters?.offset ?? 0) / 20) as any, 10) + 1);
   const formRef = useRef<FormHandles>();

   useLayoutEffect(() => {
      return registerOnInit((v: any) => {
         setSum(v.items.sum);
         setSumPage(v.items.sum_page);
      });
   }, []);

   useLayoutEffect(() => {
      return registerOnAfterSearch((v: any) => {
         setSum(v.sum);
         setSumPage(v.sum_page);
      });
   }, []);

   useLayoutEffect(() => {
      return registerOnInit((v: any) => {
         if (v.groups) {
            setGroups(v.groups.results);
         }
      });
   }, []);

   /**
    * Seller Select
    */
   const user = useUser();
   const userRole = user.role;

   const [sellerSelect, setSellerSelect] = useRecoilState(companySelectState);
   useDidUpdateEffect(() => {
      reloadPage(true);
   }, [sellerSelect]);

   useLayoutEffect(() => {
      if (!graph) {
         return;
      }

      setResource(null);
      setExpiredGraphProps(grahSettings);
      setToExpireGraphProps(grahSettings);
      setLosersBar([]);
      setToWinBar([]);
      setStatement([]);

      PaymentsSearchResolve(filters).subscribe(
         (r) => {
            setResource(graph);

            setStatement((graph as any).next);

            setToExpireGraphProps({
               ...expiredGraphProps,
               data: {
                  labels: [
                     'A vencer até 30 dias',
                     'A vencer em de 31 a 60 dias',
                     'A vencer em de 61 a 90 dias',
                     'A vencer em de 91 a 120 dias',
                     'A vencer em de 121 a 180 dias',
                     'Acima de 180 dias',
                  ],
                  prices: [
                     (graph as any).to_win['0_30'].sum,
                     (graph as any).to_win['30_60'].sum,
                     (graph as any).to_win['60_90'].sum,
                     (graph as any).to_win['90_120'].sum,
                     (graph as any).to_win['120_180'].sum,
                     (graph as any).to_win['180'].sum,
                  ],
                  datasets: [
                     {
                        label: '# of Votes',
                        data: [
                           (graph as any).to_win['0_30'].count,
                           (graph as any).to_win['30_60'].count,
                           (graph as any).to_win['60_90'].count,
                           (graph as any).to_win['90_120'].count,
                           (graph as any).to_win['120_180'].count,
                           (graph as any).to_win['180'].count,
                        ],
                        backgroundColor: ['#35a8e0', '#3097ca', '#2a86b3', '#25769d', '#206586', '#1b5470'],
                     },
                  ],
               },
            });
            const to_win: any[] = [];
            to_win.push({
               ...(graph as any).to_win['0_30'],
               name: 'A vencer até 30 dias',
               color: '#35a8e0',
               urlData: {
                  initialDate: dateFNS.format(new Date(), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.addDays(new Date(), 30), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(new Date(), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.addDays(new Date(), 30), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            to_win.push({
               ...(graph as any).to_win['30_60'],
               name: 'A vencer em de 31 a 60 dias',
               color: '#3097ca',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.addDays(new Date(), 31), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.addDays(new Date(), 60), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.addDays(new Date(), 31), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.addDays(new Date(), 60), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            to_win.push({
               ...(graph as any).to_win['60_90'],
               name: 'A vencer em de 61 a 90 dias',
               color: '#2a86b3',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.addDays(new Date(), 61), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.addDays(new Date(), 90), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.addDays(new Date(), 61), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.addDays(new Date(), 90), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            to_win.push({
               ...(graph as any).to_win['90_120'],
               name: 'A vencer em de 91 a 120 dias',
               color: '#25769d',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.addDays(new Date(), 91), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.addDays(new Date(), 120), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.addDays(new Date(), 91), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.addDays(new Date(), 120), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            to_win.push({
               ...(graph as any).to_win['120_180'],
               name: 'A vencer em de 121 a 180 dias',
               color: '#206586',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.addDays(new Date(), 121), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.addDays(new Date(), 180), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.addDays(new Date(), 121), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.addDays(new Date(), 180), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            to_win.push({
               ...(graph as any).to_win['180'],
               name: 'Acima de 180 dias',
               color: '#1b5470',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.addDays(new Date(), 180), 'DD/MM/YYYY'),
                  status: 'PENDING',
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.addDays(new Date(), 180), 'YYYYMMDD'),
                  status_group: 'next',
               },
            });
            setToWinBar(to_win);

            setExpiredGraphProps({
               ...expiredGraphProps,
               data: {
                  labels: [
                     'Vencidos a 30 dias',
                     'Vencidos de 31 a 60 dias',
                     'Vencidos de 61 a 90 dias',
                     'Vencidos de 91 a 120 dias',
                     'Vencidos de 121 a 180 dias',
                     'Vencidos a mais de 180 dias',
                  ],
                  prices: [
                     (graph as any).losers['0_30'].sum,
                     (graph as any).losers['30_60'].sum,
                     (graph as any).losers['60_90'].sum,
                     (graph as any).losers['90_120'].sum,
                     (graph as any).losers['120_180'].sum,
                     (graph as any).losers['180'].sum,
                  ],
                  datasets: [
                     {
                        data: [
                           (graph as any).losers['0_30'].count,
                           (graph as any).losers['30_60'].count,
                           (graph as any).losers['60_90'].count,
                           (graph as any).losers['90_120'].count,
                           (graph as any).losers['120_180'].count,
                           (graph as any).losers['180'].count,
                        ],
                        backgroundColor: ['#f32d32', '#db292d', '#c22428', '#aa1f23', '#921b1e', '#7a1719'],
                     },
                  ],
               },
            });

            const losers = [];
            losers.push({
               ...(graph as any).losers['0_30'],
               name: 'Vencidos a 30 dias',
               color: '#f32d32',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.subDays(new Date(), 30), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(new Date(), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.subDays(new Date(), 30), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(new Date(), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            losers.push({
               ...(graph as any).losers['30_60'],
               name: 'Vencidos de 31 a 60 dias',
               color: '#db292d',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.subDays(new Date(), 60), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.subDays(new Date(), 31), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.subDays(new Date(), 60), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.subDays(new Date(), 31), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            losers.push({
               ...(graph as any).losers['60_90'],
               name: 'Vencidos de 61 a 90 dias',
               color: '#c22428',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.subDays(new Date(), 90), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.subDays(new Date(), 61), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.subDays(new Date(), 90), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.subDays(new Date(), 61), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            losers.push({
               ...(graph as any).losers['90_120'],
               name: 'Vencidos de 91 a 120 dias',
               color: '#aa1f23',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.subDays(new Date(), 120), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.subDays(new Date(), 91), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.subDays(new Date(), 120), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.subDays(new Date(), 91), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            losers.push({
               ...(graph as any).losers['120_180'],
               name: 'Vencidos de 121 a 180 dias',
               color: '#921b1e',
               urlData: {
                  initialDate: dateFNS.format(dateFNS.subDays(new Date(), 180), 'DD/MM/YYYY'),
                  finalDate: dateFNS.format(dateFNS.subDays(new Date(), 121), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_gte: dateFNS.format(dateFNS.subDays(new Date(), 180), 'YYYYMMDD'),
                  expiration_date_lte: dateFNS.format(dateFNS.subDays(new Date(), 121), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            losers.push({
               ...(graph as any).losers['180'],
               name: 'Vencidos a mais de 180 dias',
               color: '#7a1719',
               urlData: {
                  finalDate: dateFNS.format(dateFNS.subDays(new Date(), 181), 'DD/MM/YYYY'),
               },
               url: {
                  expiration_date_lte: dateFNS.format(dateFNS.subDays(new Date(), 181), 'YYYYMMDD'),
                  status_group: 'expired',
               },
            });
            setLosersBar(losers);
         },
         () => {},
      );
   }, [graph]);

   useDidUpdateEffect(() => {
      setPage(currentPage);
   }, [currentPage]);

   useEffect(() => {
      if (formRef.current) {
         formRef.current.setData(filters);
      }
      onUnselectAll();
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
   }, [filters]);

   const filtersData = {
      types: [
         {
            id: '',
            name: 'Todos',
         },
         {
            id: 'credit',
            name: 'Cartão de crédito',
         },
         {
            id: 'boleto',
            name: 'Boleto',
         },
      ],
      groups: [
         {
            id: '',
            name: 'Todos',
         },
         ...groups.map((group) => ({
            id: group.id,
            name: group.name,
         })),
      ],
      status: [
         { id: 'next', name: 'A vencer' },
         { id: 'expired', name: 'Vencidos' },
         { id: 'pay', name: 'Pagos' },
         { id: 'failed', name: 'Falhados' },
         { id: 'cancel', name: 'Cancelados' },
      ],
   };

   function sendSMS(): void {
      setLoading(true);

      SendWhatsAppBoletoResolve(Array.from(selected.keys())).subscribe(
         () => {
            onUnselectAll();
            setLoading(false);
            ToastList.show({
               message: 'WhatsApp enviados com sucesso',
            });
         },
         () => {
            setLoading(false);
            ToastList.show({
               message: 'Erro ao enviar WhatsApp',
               type: 'error',
            });
         },
      );
   }

   function generateExcel(): void {
      if (selected.size) {
         GenerateExcel({ ids: Array.from(selected.keys()) }, 'boletos').subscribe(
            (link) => {
               setFileDownloadGlobal(link);
               // window.open(`${APIServiceUrl}/excel/${link}`);
            },
            (data) => {
               ToastList.show({
                  message: data.error,
                  type: 'error',
               });
            },
         );
      } else {
         GenerateExcel(filters, 'boletos').subscribe(
            (link) => {
               setFileDownloadGlobal(link);
            },
            (data) => {
               ToastList.show({
                  message: data.error,
                  type: 'error',
               });
            },
         );
      }
   }

   function sendOne(id: string): void {
      setLoading(true);

      SendWhatsAppBoletoResolve([id]).subscribe(
         () => {
            onUnselectAll();
            setLoading(false);
            ToastList.show({
               message: 'WhatsApp enviado com sucesso',
            });
         },
         () => {
            setLoading(false);
            ToastList.show({
               message: 'Erro ao enviar WhatsApp',
               type: 'error',
            });
         },
      );
   }

   const [modalCancel, setModalCancel] = useState(false);
   const idCancel = useRef<any>(0);

   function onCancelModal(data: any): void {
      if (!Array.isArray(idCancel.current)) {
         const confirm = window.confirm(`Tem certeza que deseja cancelar o boleto? Essa ação não poderá ser desfeita.`);

         if (!confirm) return;

         setLoading(true);
         BoletoCancelResolve(idCancel.current, data).subscribe(
            () => {
               onUnselectAll();
               setFilters(() => ({ ...filters, times: Date.now() }));
               setLoading(false);
               ToastList.show({
                  message: 'Boleto cancelado com sucesso!',
               });
               setModalCancel(false);
            },
            () => {
               setLoading(false);
               ToastList.show({
                  message: 'Erro ao cancelar boleto',
                  type: 'error',
               });
            },
         );
      } else {
         const confirm = window.confirm(
            `Tem certeza que deseja cancelar esses boletos? Essa ação não poderá ser desfeita.`,
         );

         if (!confirm) return;

         setLoading(true);

         const ids = Array.from(selected.keys());

         BoletoCancelManyResolve({
            ids,
            ...data,
         }).subscribe(
            () => {
               onUnselectAll();
               setFilters(() => ({ ...filters, times: Date.now() }));
               setLoading(false);
               setModalCancel(false);
               ToastList.show({
                  message: 'Boletos cancelados com sucesso!',
               });
            },
            () => {
               setLoading(false);
               ToastList.show({
                  message: 'Erro ao cancelar boletos',
                  type: 'error',
               });
            },
         );
      }
   }

   function cancelBoleto(id: string, cancel: boolean): void {
      idCancel.current = id;
      setModalCancel(true);
   }

   function cancelMany(id: string, cancel: boolean): void {
      idCancel.current = Array.from(selected.keys());
      setModalCancel(true);
   }

   function onSubmit(data: any) {
      const params = data;
      params.expiration_date_gte = data?.expiration_date_gte?.dateYYYYMMDD;
      params.expiration_date_lte = data?.expiration_date_lte?.dateYYYYMMDD;
      params.created_at_gte = data?.created_at_gte?.dateYYYYMMDD;
      params.created_at_lte = data?.created_at_lte?.dateYYYYMMDD;

      setFilters(() => params);
   }

   function selectAll(): void {
      if (checkedAll) {
         if (Array.from(selected.keys()).length === resources.length) {
            onUnselectAll();
         } else {
            resources.forEach((item: any) => {
               onSelect(item.id);
            });
         }
      } else {
         resources.forEach((item: any) => {
            onSelect(item.id);
         });
      }
   }

   const checkedAll = Array.from(selected.keys()).length === resources.length;

   return {
      onSubmit,
      selectAll,
      checkedAll,
      setSort,
      formRef,
      reloadPage,
      starting,
      searching,
      loading,
      errorLoadData,
      resources,
      resourcesTotal,
      skeletonResources,
      filters,
      filtersData,
      currentPage,
      setCurrentPage,
      onSelect,
      generateExcel,
      onUnselectAll,
      selected,
      sendSMS,
      sendOne,
      cancelBoleto,
      cancelMany,
      checkSelected,
      //
      // starting,
      sum,
      sumPage,
      resource,
      losersBar,
      toWinBar,
      expiredGraphProps,
      toExpireGraphProps,
      statement,
      userRole,
      modalCancel,
      setModalCancel,
      onCancelModal,
      showSeller,
   };
}
